import React, { FC, useEffect, useState } from "react";
import { message, Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { IDocumentFields } from "../../../../interfaces/ICase";
import {
  deleteUploadedDocument,
  downloadDocument,
  getAllStarhealthDocumentsData,
} from "../../../../apis/patientService";
import {
  errorMessage,
  successMessage,
} from "../../../../utils/notificationUtils";
import { rejectJfifUpload } from "../../../../utils/fileUploadValidation";
import { getEnhancementReason } from "../../../../apis/SchemeService/CGHS/cghsService";
import {
  enhancementServiceAyushman,
  fetchEnhancementDataAyushman,
  fetchProcedures,
  fetchStratifications,
} from "../../../../apis/ayushmanBService";
import { getPortalDataForAyushB } from "../../../../models/ayushmanBSlice";
import { UploadDocument } from "../../../../models/EnhancementSlice";
import {
  getBasicDetails,
  getDeficiencyQuestionData,
  getTimelineDetails,
} from "../../../../models/patientSlice";
import dayjs from "dayjs";
import FilePreviewModal from "../../../../components/patient/PreAuth-query/FilePreviewModal";
import PatientDetailsStageDocument from "../../../../components/modals/PatientDetailsStageDocument";
import EnhancementAyushman from "../../../../components/patient/Enhancement/Ayushman/EnhancementAyushman";

interface EnhancementContainerAyushmanProps {
  createEnhancement: boolean;
  closeCreateEnhancementModal: any;
  activeKey: any;
}

const EnhancementContainerAyushman: FC<EnhancementContainerAyushmanProps> = (
  props,
) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { patients, ayushBPreAuth, preAuth, enhancement, auth } = useSelector(
    (state: RootState) => state,
  );
  const [enhancementProcedure, setEnhancementProcedure] = useState<any>([]);
  const [procedureMasterList, setProcedureMasterList] = useState<any[]>([]);
  const [procedureStrats, setProcedureStrats] = useState<any>({});
  const [deleteProcedureList, setDeleteProcedureList] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });
  const [uploadResult, setUploadResult] = useState<any>([]);
  const [uploadedDocumentUrl, setUploadedDocumentUrl] = useState<
    {
      documentUrl: string;
      documentType: string;
      documentLabelType: string;
      documentName: string;
      deficiencyCheckStatus: any;
      id: number;
    }[]
  >([]);
  const [selectedFile, setSelectedFile] = useState<{
    file: File;
    documentType: string;
  } | null>(null);
  const [previewDocument, setPreviewDocument] = useState(false);
  const [deficiencyModal, setDeficiencyModal] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentFields | null>(null);
  const [caseEnhancementId] = useState<string | null>(null);
  const [clickedActiveTab, setClickedActiveTab] = useState<any>();
  const [clickedActiveDocumentUrl, setClickedActiveDocumentUrl] =
    useState<any>();
  const [isFetchingProcedures, setIsFetchingProcedures] =
    useState<boolean>(false);
  const [enhancementReasonOptions, setEnhancementReasonOptions] = useState<any>(
    [],
  );

  const fetchDocuments = async (
    schemeMetaDataId: number,
    caseId: number,
    visitId: number,
  ) => {
    try {
      if (
        patients?.fetchBasicDetails?.casesResponse?.id &&
        patients?.fetchBasicDetails?.visitInfoResponse?.id
      ) {
        const AllDocumentList = await getAllStarhealthDocumentsData(
          caseId,
          visitId,
          schemeMetaDataId,
        );
        setDocumentTypes(AllDocumentList?.["Enhancement"]);
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
    }
  };

  // Exact PreAuth procedure API:
  // POST /api/ayushmanBharat/procedures  body: { hospitalId, schemeId }
  const handleFetchProcedureMaster = async () => {
    const hospitalId =
      patients?.fetchBasicDetails?.casesResponse?.healthCareId;
    const schemeId =
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
    if (!hospitalId || !schemeId) return;

    try {
      const res = await fetchProcedures({
        hospitalId,
        schemeId,
      });
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.content)
            ? res.content
            : [];
      setProcedureMasterList(list);
    } catch (err: any) {
      setProcedureMasterList([]);
      errorMessage(err?.message || "Failed to get procedures");
    }
  };

  const getProcedureMatchKey = (proc: any) =>
    String(
      proc?.externalProcedureCode ||
        proc?.ichiCode ||
        proc?.procedureCode ||
        proc?.procedureName ||
        proc?.id ||
        "",
    )
      .trim()
      .toLowerCase();

  // Map getAllCasePreAuth treatmentPlansData → non-editable PreAuth rows
  const mapTreatmentPlansToEnhancementProcedures = (plans: any[] = []) =>
    (plans || [])
      .filter((plan) => plan?.procedure?.id || plan?.procedure?.name)
      .map((plan) => {
        const procedureName =
          plan?.procedure?.name ||
          plan?.procedure?.procedureName ||
          plan?.procedureName ||
          "";
        const ichiCode =
          plan?.ichiCode ||
          plan?.externalProcedureCode ||
          plan?.procedure?.id ||
          "";
        const numberOfDays =
          plan?.numberOfDaysPerUnits ?? plan?.numberOfDays ?? null;
        const procedureAmount =
          plan?.procedureRate ??
          plan?.procedure?.procedureAmount ??
          plan?.procedureAmount ??
          null;
        const total =
          numberOfDays != null && procedureAmount != null
            ? Number(numberOfDays) * Number(procedureAmount)
            : null;

        return {
          ...plan,
          procedureName,
          externalProcedureCode: ichiCode,
          numberOfDays,
          procedureAmount,
          procedureTotalAmount: total,
          treatmentStartDate: plan?.treatmentStartDate || "",
          stratificationName:
            plan?.stratification?.name || plan?.stratificationName || "",
          specialityName: plan?.speciality?.name || plan?.specialityName || "",
          fromPreAuth: true,
          fromPreviousEnhancement: false,
        };
      });

  const mapPreviousEnhancementProcedures = (
    procedures: any[] = [],
    enhancementCreatedAt?: string,
  ) =>
    (procedures || []).map((proc: any) => ({
      ...proc,
      procedureName:
        proc?.procedureName ||
        proc?.unspecifiedProcedureName ||
        proc?.name ||
        "",
      externalProcedureCode:
        proc?.externalProcedureCode || proc?.ichiCode || "",
      numberOfDays: proc?.numberOfDays ?? proc?.numberOfDaysPerUnits ?? null,
      procedureAmount: proc?.procedureAmount ?? proc?.procedureRate ?? null,
      stratificationName:
        proc?.stratificationName ||
        proc?.stratification?.name ||
        proc?.stratsName ||
        "",
      stratificationCode:
        proc?.stratificationCode ||
        proc?.stratification?.id ||
        null,
      fromPreAuth: proc?.fromPreAuth === true,
      fromPreviousEnhancement: true,
      createdAt: proc?.createdAt || enhancementCreatedAt || null,
    }));

  const normalizeExistingProcedureForPayload = (proc: any) => {
    const {
      id,
      fromPreviousEnhancement,
      ...rest
    } = proc || {};
    return {
      ...rest,
      numberOfDaysPerUnits: proc?.numberOfDays ?? proc?.numberOfDaysPerUnits,
      ichiCode: proc?.externalProcedureCode || proc?.ichiCode || "",
      procedureRate: proc?.procedureAmount ?? proc?.procedureRate,
      status: true,
      fromPreAuth: proc?.fromPreAuth === true,
      createdAt: proc?.createdAt || null,
    };
  };

  // Load PreAuth procedures + latest enhancement procedures for Add form
  const handleFetchExistingProcedures = async () => {
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    if (!caseId || !visitId) return;

    try {
      setIsFetchingProcedures(true);

      let preAuthPlans =
        ayushBPreAuth?.portalData?.casePreAuthOthersResponse
          ?.treatmentPlansData;

      if (!(Array.isArray(preAuthPlans) && preAuthPlans.length > 0)) {
        const res = await dispatch(
          getPortalDataForAyushB({ caseId, visitId }),
        ).unwrap();
        preAuthPlans =
          res?.casePreAuthOthersResponse?.treatmentPlansData ||
          res?.treatmentPlansData ||
          [];
      }

      const preAuthProcedures = mapTreatmentPlansToEnhancementProcedures(
        Array.isArray(preAuthPlans) ? preAuthPlans : [],
      );

      let previousEnhancementProcedures: any[] = [];
      try {
        const enhancementRes: any = await fetchEnhancementDataAyushman(
          caseId,
          visitId,
        );
        const list = Array.isArray(enhancementRes) ? enhancementRes : [];
        // API returns newest first (View uses index 0 as latest)
        const latest = list?.[0];
        previousEnhancementProcedures = mapPreviousEnhancementProcedures(
          latest?.enhancementProceduresResponseList || [],
          latest?.createdAt,
        );
      } catch (err) {
        previousEnhancementProcedures = [];
      }

      const previousKeys = new Set(
        previousEnhancementProcedures.map(getProcedureMatchKey).filter(Boolean),
      );
      // Keep PreAuth rows that are not already present in latest enhancement
      const uniquePreAuthProcedures = preAuthProcedures.filter(
        (proc: any) => !previousKeys.has(getProcedureMatchKey(proc)),
      );

      setEnhancementProcedure([
        ...uniquePreAuthProcedures,
        ...previousEnhancementProcedures,
      ]);
    } catch (err: any) {
      setEnhancementProcedure([]);
      errorMessage(
        err?.message || "Failed to load existing procedures",
      );
    } finally {
      setIsFetchingProcedures(false);
    }
  };

  useEffect(() => {
    if (!props?.createEnhancement) return;

    if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
      fetchDocuments(
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        patients?.fetchBasicDetails?.casesResponse?.id,
        patients?.fetchBasicDetails?.visitInfoResponse?.id,
      );
    }
  }, [
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
    props?.createEnhancement,
  ]);

  const formattedAccreditationOptions: Array<{ label: string; value: string }> =
    [
      { label: "NABH", value: "NABH" },
      { label: "NABL", value: "NABL" },
      { label: "NON NABL", value: "NON NABL" },
      { label: "NON NABH", value: "NON NABH" },
    ]?.map((option) => ({
      value: option?.value + "~" + option?.label,
      label: option.label,
    }));

  const formattedTreatingDoctorsOptions: Array<{
    label: string;
    value: string;
  }> = [{ treatingDoctorCode: "OTHERS", treatingDoctorName: "OTHERS" }]?.map(
    (option) => ({
      value: option?.treatingDoctorCode + "~" + option?.treatingDoctorName,
      label: option?.treatingDoctorName,
    }),
  );

  const formattedProcedureReferral: Array<{ label: string; value: string }> = [
    { label: "REFERRAL", value: "referral" },
    { label: "NON REFERRAL", value: "non referral" },
  ]?.map((option) => ({
    value: option?.value + "~" + option?.label,
    label: option.label,
  }));

  const formattedProceduresOptions: Array<{
    label: string;
    value: string;
    extra?: any;
  }> = procedureMasterList?.map((option: any) => {
    const procedureName =
      option?.procedurename || option?.procedureName || option?.name || "";
    const procedureCode =
      option?.procedurecode || option?.procedureCode || option?.id || "";
    const amount = option?.amount ?? option?.procedurecost ?? null;
    const quantityAllowed =
      option?.quantityallowed ??
      option?.quantityAllowed ??
      option?.QuantityAllowed ??
      null;
    const normalized = {
      ...option,
      procedureName,
      procedurecode: procedureCode,
      procedureCode,
      externalProcedureCode: procedureCode,
      amount,
      quantityAllowed,
      quantityallowed: quantityAllowed,
    };
    return {
      value: JSON.stringify(normalized),
      label: procedureName,
      extra: amount,
    };
  });

  const handleGetProcedureAmount = async (_formData: any) => {
    return null;
  };

  const handleGetProcedureStrats = async (formData: any) => {
    try {
      const portalMetaDataId =
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0];
      const procedureKey =
        formData?.procedureId || formData?.procedureName || "";
      if (!portalMetaDataId || !procedureKey) return null;

      const procedureStratsRes = await fetchStratifications(
        portalMetaDataId,
        procedureKey,
      );
      const options = Array.isArray(procedureStratsRes)
        ? procedureStratsRes
        : [];
      setProcedureStrats((prevStrats: any) => ({
        ...prevStrats,
        [procedureKey]: { options },
      }));
      return {
        stratsName:
          options?.[0]?.stratificationName ||
          options?.[0]?.stratificationname ||
          "",
        options,
      };
    } catch (err: any) {
      errorMessage(err?.response?.data || "Please, Try again after sometime");
      return null;
    }
  };

  const handleDeleteProcedure = async (procedure: any) => {
    try {
      setDeleteProcedureList((prevList) => [...prevList, procedure]);
      successMessage("Procedure added in archieve list");
      return true;
    } catch (err: any) {
      errorMessage(err?.response?.data || "Please, Try again after sometime");
      return false;
    }
  };

  const getTimelineDetail = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      await dispatch(
        getTimelineDetails({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        }),
      );
    }
  };

  const handleSubmitEnhancementProcedure = async (
    values: any,
    enhancementReason: any,
  ) => {
    try {
      if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
        const formData = {
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          claimId:
            ayushBPreAuth?.portalData?.casePreAuthOthersResponse?.claimId ||
            ayushBPreAuth?.portalData?.claimId ||
            null,
          registrationId: ayushBPreAuth?.portalData?.schemeRegistrationId,
          regnId: ayushBPreAuth?.portalData?.schemeRegistrationId,
          documentUrls: uploadedDocumentUrl || [],
          enhancementProceduresRequestList: values?.length > 0 ? values : [],
          deleteProcList: deleteProcedureList,
          status: true,
          dateOfAdmission: patients?.fetchBasicDetails?.visitInfoResponse?.doa,
          dod: patients?.fetchBasicDetails?.visitInfoResponse?.dod,
          enhancementReason: enhancementReason,
        };

        await enhancementServiceAyushman.saveEnhancementData(
          patients?.fetchBasicDetails?.casesResponse?.id,
          patients?.fetchBasicDetails?.visitInfoResponse?.id,
          patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
          caseEnhancementId,
          formData,
        );
        props?.closeCreateEnhancementModal();
        setUploadedDocumentUrl([]);
        setEnhancementProcedure([]);
        setDeleteProcedureList([]);
        form.resetFields();
        await getTimelineDetail();
        dispatch(
          getBasicDetails({
            id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          }),
        );
      }
    } catch (err: any) {
      errorMessage(err?.response?.data || "Please, Try again after sometime");
    }
  };

  const handleOk = (procedure: any) => {
    handleDeleteProcedure(procedure);
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleFileChange = (documentType: string) => (e: any) => {
    if (rejectJfifUpload(e)) return;
    if (Array.isArray(e)) {
      return e;
    }
    if (e?.file) {
      setUploadResult([]);
      setSelectedFile({ file: e.file, documentType: documentType });
      setPreviewFile({
        url: URL.createObjectURL(e.file),
        name: e.file.name,
        showUpload: true,
        documentType: documentType,
      });
      setPreviewModalVisible(true);
    }
    return e?.target?.files;
  };

  const handleUpload = async () => {
    const CollectPostData = new FormData();
    if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
      CollectPostData.append(
        "hospitalId",
        patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.patientResponse?.id) {
      CollectPostData.append(
        "patientId",
        patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      CollectPostData.append(
        "caseId",
        patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      CollectPostData.append(
        "visitId",
        patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
      );
    }
    CollectPostData.append("documentType", previewFile.documentType);
    if (selectedFile?.file) {
      CollectPostData.append("file", selectedFile?.file);
    }
    try {
      const documentRes = await dispatch(
        UploadDocument({
          formData: CollectPostData,
          accessToken: auth.accessToken || "",
        }),
      ).unwrap();

      if (
        documentRes?.[0]?.documentLabelType === "justification for enhancement"
      ) {
        setUploadResult(documentRes);
      }
      const updatedDocuments = [...uploadedDocumentUrl, ...documentRes];
      setUploadedDocumentUrl(updatedDocuments);
      setPreviewModalVisible(false);
      message.success(
        `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
      );
    } catch (error) {
      console.error("Failed to upload document:", error);
    }
  };

  const handleDownloadDocument = async (fileName: string, url: string) => {
    try {
      await downloadDocument(fileName, url);
      successMessage("Document Downloaded Successfully");
    } catch (err: any) {
      errorMessage("Please, Try again later");
    }
  };

  const handleOpen = (docType: any, documentUrl: any) => {
    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setClickedActiveDocumentUrl(documentUrl);
  };

  const handleDeleteList = async (
    documentType: string,
    documentUrl: string,
    _i: number,
  ) => {
    const deleteDocument = uploadedDocumentUrl?.find(
      (doc) => doc?.documentUrl === documentUrl,
    );
    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };
    const newUpdatedDocumentList = uploadedDocumentUrl?.filter(
      (doc) => doc?.documentUrl !== documentUrl,
    );
    setUploadedDocumentUrl(newUpdatedDocumentList);
    if (deleteDocument?.documentUrl) {
      try {
        await deleteUploadedDocument(deleteForm);
        message.success("Document Deleted Successfully");
      } catch (err) {
        console.error(err);
      }
    }
  };

  const fetchDeficiencyData = async (selectedDocument: any) => {
    try {
      if (selectedDocument?.id) {
        await dispatch(getDeficiencyQuestionData(selectedDocument?.id));
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
      setSelectedDocument(null);
    }
  };

  const handleDeficiencyModalOpen = async (
    documentUrl: string,
    documentId: number,
    document: IDocumentFields,
  ) => {
    await fetchDeficiencyData(document);
    setDeficiencyModal(true);
    setSelectedDocument(document);
  };

  const closeDeficiencyModal = () => {
    setDeficiencyModal(false);
    setSelectedDocument(null);
  };

  const handleCancelPreview = () => {
    setPreviewDocument(false);
  };

  const validateRequiredDocuments = (
    mandatoryDocuments: any,
    documentUrls: any
  ) => {
    for (const doc of mandatoryDocuments) {
      if (doc.required) {
        const isUploaded = documentUrls?.some(
          (uploadedDoc: any) => uploadedDoc.documentLabelType === doc.label
        );
        if (!isUploaded) {
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const newItems = values?.items || [];

      if (newItems.length > 0) {
        const missingDocumentIndex = newItems.findIndex(
          (item: any) => !item?.documents || item?.documents?.length === 0,
        );

        if (missingDocumentIndex !== -1) {
          errorMessage(`Document is mandatory for items`);
          return;
        }

        const invalidQtyItem = newItems.find((item: any) => {
          const qty = Number(item?.numberOfDays);
          return !Number.isFinite(qty) || qty < 1;
        });

        if (invalidQtyItem) {
          errorMessage("Please enter a valid quantity (minimum 1)");
          return;
        }
      }

      if (!validateRequiredDocuments(documentTypes, uploadedDocumentUrl)) {
        errorMessage("Please, Upload all required documents");
        return;
      }

      const deletedKeys = new Set(
        (deleteProcedureList || [])
          .map(getProcedureMatchKey)
          .filter(Boolean),
      );

      const previousForPayload = (enhancementProcedure || [])
        .filter((proc: any) => {
          const key = getProcedureMatchKey(proc);
          return key ? !deletedKeys.has(key) : true;
        })
        .map((proc: any) => normalizeExistingProcedureForPayload(proc));

      const updatedValues = newItems.map((value: any) => {
        const parsedProcedureCode = value?.procedureCode
          ? JSON.parse(value.procedureCode)
          : {};
        let parsedStrat: any = {};
        try {
          parsedStrat = value?.stratOptionValue
            ? JSON.parse(value.stratOptionValue)
            : {};
        } catch {
          parsedStrat = {};
        }
        const stratificationCode =
          value?.stratificationCode ||
          parsedStrat?.stratificationCode ||
          parsedStrat?.stratificationcode ||
          parsedStrat?.id ||
          null;
        const stratificationName =
          value?.stratificationName ||
          parsedStrat?.stratificationName ||
          parsedStrat?.stratificationname ||
          parsedStrat?.name ||
          null;

        let result: any = {
          ...value,
          procedureReferral: value?.procedureReferral?.split("~")?.[0],
          procedureReferralName: value?.procedureReferral?.split("~")?.[1],
          accreditationStatus: value?.accreditationStatus?.split("~")?.[0],
          accreditationStatusName: value?.accreditationStatus?.split("~")?.[1],
          treatingDoctorCode: value?.treatingDoctor?.split("~")?.[0],
          treatingDoctor: value?.treatingDoctor?.split("~")?.[1],
          treatmentStartDate: value?.treatmentStartDate
            ? dayjs(value?.treatmentStartDate).format("DD-MM-YYYY HH:mm:ss")
            : null,
          ...parsedProcedureCode,
          stratsId: parsedProcedureCode?.id,
          procedureAmount: value?.procedureAmount,
          numberOfDaysPerUnits: value?.numberOfDays,
          ichiCode:
            value?.externalProcedureCode ||
            parsedProcedureCode?.externalProcedureCode ||
            "",
          procedureRate: value?.procedureAmount,
          stratification: stratificationCode
            ? { id: stratificationCode, name: stratificationName }
            : null,
          stratificationCode,
          stratificationName,
          status: true,
          fromPreAuth: false,
        };
        if (value?.stratOptionValue && Object.keys(parsedStrat).length) {
          result = { ...result, ...parsedStrat };
        }
        delete result?.["id"];
        return result;
      });

      // Previous (PreAuth + latest enhancement) + newly added → full payload
      await handleSubmitEnhancementProcedure(
        [...previousForPayload, ...updatedValues],
        values?.enhancementReason,
      );
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage("Please fill all details before submitting");
      }
    }
  };

  const handleGetEnhancementReason = async () => {
    try {
      // Temporary: reuse CGHS New enhancement reason API for Ayushman
      const res = await getEnhancementReason();
      const formattedOptions = res?.reasonslist?.map((option: any) => ({
        value: option?.reasoncode,
        label: option?.reasondesc,
      }));

      setEnhancementReasonOptions(formattedOptions);
    } catch (err: any) {
      errorMessage(err?.response?.data || "Please, Try again after sometime");
    }
  };

  useEffect(() => {
    if (props?.createEnhancement) {
      handleFetchProcedureMaster(); // POST /procedures → add-new dropdown
      handleFetchExistingProcedures(); // PreAuth + latest enhancement rows
      handleGetEnhancementReason();
    }
  }, [
    props?.createEnhancement,
    patients?.fetchBasicDetails?.casesResponse?.healthCareId,
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
  ]);

  return (
    <div style={{ padding: "14px" }}>
      <EnhancementAyushman
        addNewEnhancementModal={props?.createEnhancement}
        closeCreateEnhancementModal={props?.closeCreateEnhancementModal}
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
        isFetchingPortalData={isFetchingProcedures}
        enhancementProcedure={enhancementProcedure}
        formattedAccreditationOptions={formattedAccreditationOptions}
        formattedTreatingDoctorsOptions={formattedTreatingDoctorsOptions}
        formattedProcedureReferral={formattedProcedureReferral}
        formattedProceduresOptions={formattedProceduresOptions}
        handleGetProcedureAmount={handleGetProcedureAmount}
        handleGetProcedureStrats={handleGetProcedureStrats}
        patientDetails={patients?.fetchBasicDetails}
        procedureStrats={procedureStrats}
        handleDeleteProcedure={handleDeleteProcedure}
        handleSubmitEnhancementProcedure={handleSubmitEnhancementProcedure}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        handleOk={handleOk}
        form={form}
        documentTypes={documentTypes}
        documentUrls={uploadedDocumentUrl}
        handleFileChange={handleFileChange}
        handleOpen={handleOpen}
        enhancementReasonOptions={enhancementReasonOptions}
        uploadResult={uploadResult}
        setUploadResult={setUploadResult}
      />
      <FilePreviewModal
        isVisible={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        fileUrl={previewFile.url}
        fileName={previewFile.name}
        documentType={previewFile.documentType}
        handleUpload={handleUpload}
        showUploadButton={previewFile.showUpload}
        key={selectedFile?.file?.lastModified || "no-file"}
        isLoading={enhancement?.isLoading}
      />

      <PatientDetailsStageDocument
        handleCancel={handleCancelPreview}
        handleDeleteDocument={handleDeleteList}
        open={previewDocument}
        handleDownloadDocument={handleDownloadDocument}
        documents={uploadedDocumentUrl}
        handleDeficiencyModal={handleDeficiencyModalOpen}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        setDocumentName={setDocumentName}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setUploadedDocumentUrl}
        activeKey={props?.activeKey}
        clickedActiveTab={clickedActiveTab}
        clickedActiveDocumentUrl={clickedActiveDocumentUrl}
        isIntimated={
          preAuth?.stepperData?.visitStages?.find(
            (stage: any) =>
              String(stage?.visitStageId) === String(props?.activeKey),
          )?.intimated
        }
      />
    </div>
  );
};

export default EnhancementContainerAyushman;
